import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  CopyToClipboard,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  StatusIndicator,
  Tabs,
} from '@cloudscape-design/components';
import { CONFIG } from '../config';
import { triggerFulfillment, type ProvisionResult } from '../api/fulfillment';

export function Settings() {
  const [testEmail, setTestEmail] = useState('');
  const [testCompany, setTestCompany] = useState('');
  const [testProduct, setTestProduct] = useState('aeos-pc3');
  const [testAccountId, setTestAccountId] = useState('');
  const [testResult, setTestResult] = useState<ProvisionResult | null>(null);
  const [testing, setTesting] = useState(false);

  const mpFulfillmentUrl = `${CONFIG.API_URL}/mp/subscribe`;
  const opsApiUrl = CONFIG.API_URL;

  async function runTestFlow() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await triggerFulfillment({
        marketplaceCustomerId: `test-${Date.now()}`,
        productCode: testProduct,
        customerEmail: testEmail,
        companyName: testCompany,
      });
      setTestResult(result);
    } catch (err) {
      console.error('Test flow failed:', err);
    } finally {
      setTesting(false);
    }
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Peregrine configuration and deployment testing">
        Settings
      </Header>

      <Tabs tabs={[
        {
          id: 'test',
          label: 'Test Deployment',
          content: (
            <SpaceBetween size="l">
              <Alert type="info">
                Use this panel to simulate the full MP → onboarding → deployment flow.
                The "Simulate MP Event" button on the Fulfillment page opens the redirect URL directly.
                This form gives you more control over the test parameters.
              </Alert>

              <Container header={<Header variant="h2">Simulate Tenant Onboarding</Header>}>
                <SpaceBetween size="m">
                  <ColumnLayout columns={2}>
                    <FormField label="Company Name">
                      <Input value={testCompany} onChange={({ detail }) => setTestCompany(detail.value)} placeholder="Test Partner Corp" />
                    </FormField>
                    <FormField label="Contact Email">
                      <Input value={testEmail} onChange={({ detail }) => setTestEmail(detail.value)} placeholder="admin@testpartner.com" type="email" />
                    </FormField>
                  </ColumnLayout>
                  <ColumnLayout columns={2}>
                    <FormField label="Product">
                      <Select
                        selectedOption={{ label: testProduct === 'aeos-pc3' ? 'AEOS PC3' : '2ndFactors', value: testProduct }}
                        onChange={({ detail }) => setTestProduct(detail.selectedOption.value || 'aeos-pc3')}
                        options={[
                          { label: 'AEOS PC3', value: 'aeos-pc3' },
                          { label: '2ndFactors', value: '2ndfactors' },
                        ]}
                      />
                    </FormField>
                    <FormField label="Target AWS Account ID" description="Optional — simulates MP providing the account ID">
                      <Input value={testAccountId} onChange={({ detail }) => setTestAccountId(detail.value)} placeholder="730335467631" />
                    </FormField>
                  </ColumnLayout>

                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      variant="primary"
                      loading={testing}
                      disabled={!testEmail || !testCompany}
                      onClick={runTestFlow}
                    >
                      Provision & Get Onboarding URL
                    </Button>
                    <Button
                      onClick={() => window.open(`${CONFIG.API_URL}/mp/subscribe`, '_blank')}
                      iconName="external"
                    >
                      Fire MP Redirect (browser)
                    </Button>
                  </SpaceBetween>

                  {testResult && (
                    <Container>
                      <SpaceBetween size="s">
                        <StatusIndicator type="success">Tenant provisioned</StatusIndicator>
                        <div>
                          <Box variant="awsui-key-label">Tenant ID</Box>
                          <Box><code>{testResult.tenantId}</code></Box>
                        </div>
                        <div>
                          <Box variant="awsui-key-label">Onboarding URL</Box>
                          <Box>
                            <a href={testResult.onboardingUrl} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                              {testResult.onboardingUrl}
                            </a>
                          </Box>
                        </div>
                        <Button
                          iconName="external"
                          onClick={() => window.open(testResult.onboardingUrl, '_blank')}
                        >
                          Open Onboarding Portal
                        </Button>
                      </SpaceBetween>
                    </Container>
                  )}
                </SpaceBetween>
              </Container>

              <Container header={<Header variant="h2">Cross-Account Deployment</Header>}>
                <SpaceBetween size="m">
                  <Box variant="p">
                    To deploy into an existing account (yours, a partner's, or a customer's),
                    the target account needs a trust role that allows Peregrine to deploy CloudFormation.
                    The onboarding Step 3 generates a CFN deep-link that creates this role automatically.
                  </Box>
                  <Box variant="p">
                    For testing against your own GDNA account ({`730335467631`}), you can use the
                    "Provision & Get Onboarding URL" button above and walk through the onboarding wizard.
                    The CFN deep-link in Step 3 will point at your account.
                  </Box>
                  <Alert type="warning">
                    The CloudFormation template at <code>peregrine-templates-dev</code> S3 bucket doesn't exist yet.
                    Step 3 generates a valid deep-link URL, but the actual stack template needs to be built
                    in the AEOS PC3 repo and uploaded to S3 before the CFN launch works for real.
                  </Alert>
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          ),
        },
        {
          id: 'endpoints',
          label: 'API Endpoints',
          content: (
            <SpaceBetween size="m">
              <Container header={<Header variant="h2">Peregrine API</Header>}>
                <SpaceBetween size="s">
                  <div>
                    <Box variant="awsui-key-label">Ops API</Box>
                    <Box><code>{opsApiUrl}</code></Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">MP Fulfillment URL (register in Marketplace)</Box>
                    <Box><code>{mpFulfillmentUrl}</code></Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Environment</Box>
                    <Box>{import.meta.env.VITE_ENVIRONMENT || 'dev'}</Box>
                  </div>
                </SpaceBetween>
              </Container>

              <Container header={<Header variant="h2">Cognito Pools</Header>}>
                <ColumnLayout columns={2}>
                  <div>
                    <Box variant="awsui-key-label">ABP Internal Pool (operators)</Box>
                    <Box><code>us-east-1_ekLT9M2GN</code></Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Customer Pool (CC customers)</Box>
                    <Box><code>us-east-1_3f2PU5Nqv</code></Box>
                  </div>
                </ColumnLayout>
              </Container>

              <Container header={<Header variant="h2">curl Examples</Header>}>
                <SpaceBetween size="s">
                  <Box variant="awsui-key-label">Provision tenant via API</Box>
                  <Box>
                    <code style={{ fontSize: 12, display: 'block', background: '#F3F4F6', padding: 12, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{`curl -X POST ${opsApiUrl}/ops/fulfillment \\
  -H 'Content-Type: application/json' \\
  -d '{"marketplaceCustomerId":"test-001","productCode":"aeos-pc3","customerEmail":"admin@partner.com","companyName":"Partner Corp","awsAccountId":"123456789012"}'`}
                    </code>
                  </Box>
                  <Box variant="awsui-key-label">Simulate MP browser redirect</Box>
                  <Box>
                    <code style={{ fontSize: 12, display: 'block', background: '#F3F4F6', padding: 12, borderRadius: 6 }}>
                      {mpFulfillmentUrl}
                    </code>
                  </Box>
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          ),
        },
        {
          id: 'config',
          label: 'Configuration',
          content: (
            <ColumnLayout columns={2}>
              <Container header={<Header variant="h2">Marketplace</Header>}>
                <SpaceBetween size="s">
                  <Box variant="awsui-key-label">MP Product Code</Box>
                  <Box>Not yet registered</Box>
                  <Box variant="awsui-key-label">Fulfillment URL</Box>
                  <Box><code>{mpFulfillmentUrl}</code></Box>
                </SpaceBetween>
              </Container>
              <Container header={<Header variant="h2">Templates S3 Bucket</Header>}>
                <SpaceBetween size="s">
                  <Box variant="awsui-key-label">Bucket</Box>
                  <Box><code>peregrine-templates-dev</code></Box>
                  <Box variant="awsui-key-label">Status</Box>
                  <StatusIndicator type="warning">Not created yet</StatusIndicator>
                </SpaceBetween>
              </Container>
            </ColumnLayout>
          ),
        },
      ]} />
    </SpaceBetween>
  );
}
